import { IndicatorInput_InputType } from '../proto/generated/indicator'
import { ChartPatternKind } from '../util/IndicatorUtils/patterns/chartPatternTypes'
import { CHART_PATTERN_PIVOT_LENGTH } from '../util/IndicatorUtils/patterns/patternUtils'

/** TradingView Auto Chart Patterns — visibility filters */
export const CHART_PATTERN_STATUS_FILTER_OPTIONS = [
    'All',
    'Awaiting and Reached',
    'Only Awaiting',
    'Last Awaiting',
] as const

export const CHART_PATTERN_PRICE_TARGET_FILTER_OPTIONS = [
    'All',
    'Only Awaiting',
    'Awaiting and Reached',
    'None',
] as const

export const CHART_PATTERN_SECOND_PEAK_OPTIONS = ['Higher', 'Lower', 'Both'] as const

export const CHART_PATTERN_DEFAULT_COLOR = '#9C27B0'

/** TradingView auto chart pattern theme colors */
export const CHART_PATTERN_DOUBLE_TOP_COLOR = '#F23645'
export const CHART_PATTERN_DOUBLE_BOTTOM_COLOR = '#089981'
export const CHART_PATTERN_RISING_WEDGE_COLOR = '#22C55E'
export const CHART_PATTERN_FALLING_WEDGE_COLOR = '#F23645'
export const CHART_PATTERN_BEARISH_PENNANT_COLOR = '#F23645'
export const CHART_PATTERN_BULLISH_PENNANT_COLOR = '#089981'

/** TradingView auto chart patterns that expose a measured-move Target line */
export const CHART_PATTERNS_WITH_PRICE_TARGETS = new Set<ChartPatternKind>([
    'HEAD_AND_SHOULDERS',
    'DOUBLE_TOP',
    'DOUBLE_BOTTOM',
    'CUP_AND_HANDLE',
    'RISING_WEDGE',
    'FALLING_WEDGE',
    'BEARISH_PENNANT',
    'BULLISH_PENNANT',
    'ASCENDING_TRIANGLE',
    'DESCENDING_TRIANGLE',
])

export const normalizeChartPatternCode = (code: string): ChartPatternKind =>
    code.replace(/\W/g, '_').toUpperCase() as ChartPatternKind

export const chartPatternSupportsPriceTarget = (code: string): boolean =>
    CHART_PATTERNS_WITH_PRICE_TARGETS.has(normalizeChartPatternCode(code))

const CHART_PATTERN_PATTERNS_VISIBILITY_INPUT = {
    title: 'Patterns',
    code: 'PATTERNS',
    type: IndicatorInput_InputType.STRING,
    defval: 'All',
    minval: '',
    maxval: '',
    options: [...CHART_PATTERN_STATUS_FILTER_OPTIONS],
    group: 'Visibility',
    tooltip: '',
    display: true,
}

const CHART_PATTERN_PRICE_TARGETS_VISIBILITY_INPUT = {
    title: 'Price Targets',
    code: 'PRICE_TARGETS',
    type: IndicatorInput_InputType.STRING,
    defval: 'All',
    minval: '',
    maxval: '',
    options: [...CHART_PATTERN_PRICE_TARGET_FILTER_OPTIONS],
    group: 'Visibility',
    tooltip: '',
    display: true,
}

/** H&S always includes Price Targets on TradingView */
export const CHART_PATTERN_VISIBILITY_INPUTS = [
    CHART_PATTERN_PATTERNS_VISIBILITY_INPUT,
    CHART_PATTERN_PRICE_TARGETS_VISIBILITY_INPUT,
]

export const buildChartPatternVisibilityInputs = (code: string) => {
    const inputs: (typeof CHART_PATTERN_VISIBILITY_INPUTS)[number][] = [CHART_PATTERN_PATTERNS_VISIBILITY_INPUT]
    if (chartPatternSupportsPriceTarget(code)) {
        inputs.push(CHART_PATTERN_PRICE_TARGETS_VISIBILITY_INPUT)
    }
    return inputs
}

export const CHART_PATTERN_TREND_HEIGHT_INPUT = {
    title: 'Trend Height',
    code: 'TREND_HEIGHT',
    type: IndicatorInput_InputType.INTEGER,
    defval: '50',
    minval: '1',
    maxval: '',
    options: [],
    group: 'Settings',
    tooltip: '',
    display: true,
}

export const CHART_PATTERN_IN_PROGRESS_INPUT = {
    title: 'In Progress',
    code: 'IN_PROGRESS',
    type: IndicatorInput_InputType.BOOLEAN,
    defval: 'true',
    minval: '',
    maxval: '',
    options: [],
    group: 'Settings',
    tooltip: '',
    display: true,
}

export const CHART_PATTERN_PERMISSIBLE_DEVIATION_INPUT = {
    title: 'Permissible Deviation',
    code: 'PERMISSIBLE_DEVIATION',
    type: IndicatorInput_InputType.INTEGER,
    defval: '15',
    minval: '1',
    maxval: '',
    options: [],
    group: 'Settings',
    tooltip: '',
    display: true,
}

/** TradingView Rising Wedge — Channel Width Ratio Start/End (percent) */
export const CHART_PATTERN_CHANNEL_WIDTH_RATIO_INPUT = {
    title: 'Channel Width Ratio Start/End',
    code: 'CHANNEL_WIDTH_RATIO',
    type: IndicatorInput_InputType.FLOAT,
    defval: '10',
    minval: '1',
    maxval: '',
    options: [],
    group: 'Settings',
    tooltip: 'Minimum required channel height difference between start and end of the wedge, as a percent.',
    display: true,
}

export const isRisingWedgePatternCode = (code: string): boolean =>
    normalizeChartPatternCode(code) === 'RISING_WEDGE'

/** TradingView Bearish / Bullish Pennant — max rollback of point 3 (% of flagpole height) */
export const CHART_PATTERN_MAX_ROLLBACK_INPUT = {
    title: 'Max Rollback',
    code: 'MAX_ROLLBACK',
    type: IndicatorInput_InputType.INTEGER,
    defval: '50',
    minval: '1',
    maxval: '',
    options: [],
    group: 'Settings',
    tooltip: '',
    display: true,
}

export const isPennantPatternCode = (code: string): boolean => {
    const normalized = code.replace(/\W/g, '_').toUpperCase()
    return normalized === 'BEARISH_PENNANT' || normalized === 'BULLISH_PENNANT'
}

const patternSpecificInputs: Partial<Record<ChartPatternKind, typeof CHART_PATTERN_PERMISSIBLE_DEVIATION_INPUT[]>> = {
    DOUBLE_TOP: [
        {
            title: 'Second Top',
            code: 'SECOND_TOP',
            type: IndicatorInput_InputType.STRING,
            defval: 'Both',
            minval: '',
            maxval: '',
            options: [...CHART_PATTERN_SECOND_PEAK_OPTIONS],
            group: 'Settings',
            tooltip: '',
            display: true,
        },
        CHART_PATTERN_PERMISSIBLE_DEVIATION_INPUT,
    ],
    DOUBLE_BOTTOM: [
        {
            title: 'Second Bottom',
            code: 'SECOND_BOTTOM',
            type: IndicatorInput_InputType.STRING,
            defval: 'Both',
            minval: '',
            maxval: '',
            options: [...CHART_PATTERN_SECOND_PEAK_OPTIONS],
            group: 'Settings',
            tooltip: '',
            display: true,
        },
        CHART_PATTERN_PERMISSIBLE_DEVIATION_INPUT,
    ],
    RISING_WEDGE: [CHART_PATTERN_CHANNEL_WIDTH_RATIO_INPUT],
    FALLING_WEDGE: [CHART_PATTERN_PERMISSIBLE_DEVIATION_INPUT],
    BEARISH_PENNANT: [CHART_PATTERN_MAX_ROLLBACK_INPUT],
    BULLISH_PENNANT: [CHART_PATTERN_MAX_ROLLBACK_INPUT],
}

/** TradingView ascending / descending triangle — no Trend Height input */
export const isAscendingTrianglePatternCode = (code: string): boolean =>
    code.replace(/\W/g, '_').toUpperCase() === 'ASCENDING_TRIANGLE'

export const isDescendingTrianglePatternCode = (code: string): boolean =>
    code.replace(/\W/g, '_').toUpperCase() === 'DESCENDING_TRIANGLE'

export const isTradingViewLhrTrianglePatternCode = (code: string): boolean => {
    const normalized = code.replace(/\W/g, '_').toUpperCase()
    return normalized === 'ASCENDING_TRIANGLE' || normalized === 'DESCENDING_TRIANGLE'
}

const intInput = (
    title: string,
    code: string,
    defval: string,
    group: string,
    minval = '1',
    tooltip = '',
) => ({
    title,
    code,
    type: IndicatorInput_InputType.INTEGER,
    defval,
    minval,
    maxval: '',
    options: [],
    group,
    tooltip,
    display: true,
})

const floatInput = (
    title: string,
    code: string,
    defval: string,
    group: string,
    minval = '0',
    tooltip = '',
) => ({
    title,
    code,
    type: IndicatorInput_InputType.FLOAT,
    defval,
    minval,
    maxval: '',
    options: [],
    group,
    tooltip,
    display: true,
})

const boolInput = (title: string, code: string, defval: string, group: string) => ({
    title,
    code,
    type: IndicatorInput_InputType.BOOLEAN,
    defval,
    minval: '',
    maxval: '',
    options: [],
    group,
    tooltip: '',
    display: true,
})

/** TradingView Ascending Triangle inputs */
export const ASCENDING_TRIANGLE_DETECTION_INPUTS = [
    intInput('Swing Length', 'SWING_LENGTH', '3', 'Detection'),
    intInput('Min Bars for Pattern', 'MIN_BARS_FOR_PATTERN', '8', 'Detection'),
    intInput('Max Bars for Pattern', 'MAX_BARS_FOR_PATTERN', '60', 'Detection'),
]

export const ASCENDING_TRIANGLE_CONFIRMATION_INPUTS = [
    intInput('Max Bars for Confirmation', 'MAX_BARS_FOR_CONFIRMATION', '9', 'Confirmation'),
]

export const ASCENDING_TRIANGLE_GENERAL_INPUTS = [
    intInput('ATR Length', 'ATR_LENGTH', '14', 'General'),
]

export const ASCENDING_TRIANGLE_VALIDATION_INPUTS = [
    floatInput('Min Swing Prominence (×ATR)', 'MIN_SWING_PROMINENCE_ATR', '0.1', 'Validation'),
    floatInput('Min Triangle Height (×ATR)', 'MIN_TRIANGLE_HEIGHT_ATR', '0.5', 'Validation'),
    floatInput('Max Triangle Height (×ATR)', 'MAX_TRIANGLE_HEIGHT_ATR', '4', 'Validation'),
    floatInput('Max Horizontal Tolerance (×ATR)', 'MAX_HORIZONTAL_TOLERANCE_ATR', '0.5', 'Validation'),
]

export const ASCENDING_TRIANGLE_DISPLAY_INPUTS = [
    boolInput('Show Labels', 'SHOW_LABELS', 'true', 'Display'),
    boolInput('Show Pattern Lines', 'SHOW_PATTERN_LINES', 'true', 'Display'),
    boolInput('Show Trendlines', 'SHOW_TRENDLINES', 'true', 'Display'),
]

export const ASCENDING_TRIANGLE_INPUTS = [
    ...ASCENDING_TRIANGLE_DETECTION_INPUTS,
    ...ASCENDING_TRIANGLE_CONFIRMATION_INPUTS,
    ...ASCENDING_TRIANGLE_GENERAL_INPUTS,
    ...ASCENDING_TRIANGLE_VALIDATION_INPUTS,
    ...ASCENDING_TRIANGLE_DISPLAY_INPUTS,
    CHART_PATTERN_IN_PROGRESS_INPUT,
]

/** Shared TradingView inputs for ascending / descending triangle */
export const TRIANGLE_PATTERN_INPUTS = ASCENDING_TRIANGLE_INPUTS

export const buildChartPatternIndicatorInputs = (code: string) => {
    const normalized = normalizeChartPatternCode(code)
    const specific = patternSpecificInputs[normalized] ?? []
    if (isTradingViewLhrTrianglePatternCode(code)) {
        return [
            ...buildChartPatternVisibilityInputs(code),
            ...TRIANGLE_PATTERN_INPUTS,
        ]
    }
    if (isRisingWedgePatternCode(code)) {
        return [
            ...buildChartPatternVisibilityInputs(code),
            CHART_PATTERN_CHANNEL_WIDTH_RATIO_INPUT,
            CHART_PATTERN_IN_PROGRESS_INPUT,
        ]
    }
    if (isPennantPatternCode(code)) {
        return [
            ...buildChartPatternVisibilityInputs(code),
            CHART_PATTERN_MAX_ROLLBACK_INPUT,
            CHART_PATTERN_IN_PROGRESS_INPUT,
        ]
    }
    return [
        ...buildChartPatternVisibilityInputs(code),
        CHART_PATTERN_TREND_HEIGHT_INPUT,
        ...specific,
        CHART_PATTERN_IN_PROGRESS_INPUT,
    ]
}

export const buildChartPatternDrawingSettings = (
    color: string = CHART_PATTERN_DEFAULT_COLOR,
    code?: string,
) => ({
    priceLineColor: color,
    priceLineStyle: 'solid',
    priceLineWidth: '2',
    neckLineColor: color,
    neckLineStyle: 'dotted',
    neckLineWidth: '2',
    showBackground: 'true',
    backgroundColor: `${color}33`,
    showPaneLabels: 'true',
    paneLabelColor: color,
    paneLabelTextColor: '#FFFFFF',
    showLines: 'true',
    showPolylines: 'true',
    showLineFills: 'true',
    showLabels: 'true',
    showPatternLines: 'true',
    showTrendlines: 'true',
    showPriceTarget: code && !chartPatternSupportsPriceTarget(code) ? 'false' : 'true',
    indicator: 'true',
})

export const buildChartPatternDetectOptionsScript = (code: string) => {
    const normalized = code.replace(/\W/g, '_').toUpperCase()
    if (normalized === 'ASCENDING_TRIANGLE' || normalized === 'DESCENDING_TRIANGLE') {
        return `{
    inProgress,
    patternsFilter,
    priceTargetsFilter,
    swingLength,
    minBarsForPattern,
    maxBarsForPattern,
    maxBarsForConfirmation,
    atrLength,
    minSwingProminenceAtr,
    minTriangleHeightAtr,
    maxTriangleHeightAtr,
    maxHorizontalToleranceAtr,
    close,
}`
    }
    if (normalized === 'RISING_WEDGE') {
        return `{
    inProgress,
    patternsFilter,
    priceTargetsFilter,
    channelWidthRatioPct: channelWidthRatio,
    close,
}`
    }
    if (normalized === 'FALLING_WEDGE') {
        return `{
    inProgress,
    patternsFilter,
    priceTargetsFilter,
    channelWidthRatioPct: channelWidthRatio,
    tolerancePct: permissibleDeviation,
    close,
}`
    }
    if (normalized === 'BEARISH_PENNANT' || normalized === 'BULLISH_PENNANT') {
        return `{
    inProgress,
    patternsFilter,
    priceTargetsFilter,
    maxRollbackPct: maxRollback,
    close,
}`
    }
    const lines = [
        '    trendHeight,',
        '    inProgress,',
        '    patternsFilter,',
    ]
    if (chartPatternSupportsPriceTarget(code)) {
        lines.push('    priceTargetsFilter,')
    }
    if (normalized === 'DOUBLE_TOP') {
        lines.push('    secondTop,', '    tolerancePct: permissibleDeviation,')
    } else if (normalized === 'DOUBLE_BOTTOM') {
        lines.push('    secondBottom,', '    tolerancePct: permissibleDeviation,')
    } else if (
        normalized.endsWith('_WEDGE')
    ) {
        lines.push('    tolerancePct: permissibleDeviation,')
    }
    lines.push('    close,')
    return `{\n${lines.join('\n')}\n}`
}

export const buildChartPatternVisibilityInputScript = (code: string) => {
    const lines = [`const patternsFilter = input.string({
    title: "Patterns",
    code: "PATTERNS",
    defval: "All",
    options: ["All", "Awaiting and Reached", "Only Awaiting", "Last Awaiting"],
    group: GRP_VISIBILITY
});`]
    if (chartPatternSupportsPriceTarget(code)) {
        lines.push(`const priceTargetsFilter = input.string({
    title: "Price Targets",
    code: "PRICE_TARGETS",
    defval: "All",
    options: ["All", "Only Awaiting", "Awaiting and Reached", "None"],
    group: GRP_VISIBILITY
});`)
    }
    return lines.join('\n')
}

export const buildChartPatternSettingsInputScript = (code: string) => {
    const normalized = code.replace(/\W/g, '_').toUpperCase()
    const lines: string[] = []

    if (normalized === 'ASCENDING_TRIANGLE' || normalized === 'DESCENDING_TRIANGLE') {
        lines.push(`const swingLength = input.int({
    title: "Swing Length",
    code: "SWING_LENGTH",
    defval: 3,
    minval: 1,
    group: "Detection"
});
const minBarsForPattern = input.int({
    title: "Min Bars for Pattern",
    code: "MIN_BARS_FOR_PATTERN",
    defval: 8,
    minval: 1,
    group: "Detection"
});
const maxBarsForPattern = input.int({
    title: "Max Bars for Pattern",
    code: "MAX_BARS_FOR_PATTERN",
    defval: 60,
    minval: 1,
    group: "Detection"
});
const maxBarsForConfirmation = input.int({
    title: "Max Bars for Confirmation",
    code: "MAX_BARS_FOR_CONFIRMATION",
    defval: 9,
    minval: 1,
    group: "Confirmation"
});
const atrLength = input.int({
    title: "ATR Length",
    code: "ATR_LENGTH",
    defval: 14,
    minval: 1,
    group: "General"
});
const minSwingProminenceAtr = input.float({
    title: "Min Swing Prominence (×ATR)",
    code: "MIN_SWING_PROMINENCE_ATR",
    defval: 0.1,
    minval: 0,
    group: "Validation"
});
const minTriangleHeightAtr = input.float({
    title: "Min Triangle Height (×ATR)",
    code: "MIN_TRIANGLE_HEIGHT_ATR",
    defval: 0.5,
    minval: 0,
    group: "Validation"
});
const maxTriangleHeightAtr = input.float({
    title: "Max Triangle Height (×ATR)",
    code: "MAX_TRIANGLE_HEIGHT_ATR",
    defval: 4,
    minval: 0,
    group: "Validation"
});
const maxHorizontalToleranceAtr = input.float({
    title: "Max Horizontal Tolerance (×ATR)",
    code: "MAX_HORIZONTAL_TOLERANCE_ATR",
    defval: 0.5,
    minval: 0,
    group: "Validation"
});
const showLabels = input.bool({
    title: "Show Labels",
    code: "SHOW_LABELS",
    defval: true,
    group: "Display"
});
const showPatternLines = input.bool({
    title: "Show Pattern Lines",
    code: "SHOW_PATTERN_LINES",
    defval: true,
    group: "Display"
});
const showTrendlines = input.bool({
    title: "Show Trendlines",
    code: "SHOW_TRENDLINES",
    defval: true,
    group: "Display"
});
const inProgress = input.bool({
    title: "In Progress",
    code: "IN_PROGRESS",
    defval: true,
    group: "Display"
});`)
        return lines.join('\n')
    }

    if (normalized === 'RISING_WEDGE') {
        lines.push(`const channelWidthRatio = input.float({
    title: "Channel Width Ratio Start/End",
    code: "CHANNEL_WIDTH_RATIO",
    defval: 10,
    minval: 1,
    group: GRP_SETTINGS
});`)
        lines.push(`const inProgress = input.bool({
    title: "In Progress",
    code: "IN_PROGRESS",
    defval: true,
    group: GRP_SETTINGS
});`)
        return lines.join('\n')
    }

    if (normalized === 'FALLING_WEDGE') {
        lines.push(`const channelWidthRatio = input.float({
    title: "Channel Width Ratio Start/End",
    code: "CHANNEL_WIDTH_RATIO",
    defval: 10,
    minval: 1,
    group: GRP_SETTINGS
});`)
        lines.push(`const permissibleDeviation = input.int({
    title: "Permissible Deviation",
    code: "PERMISSIBLE_DEVIATION",
    defval: 15,
    minval: 1,
    group: GRP_SETTINGS
});`)
        lines.push(`const inProgress = input.bool({
    title: "In Progress",
    code: "IN_PROGRESS",
    defval: true,
    group: GRP_SETTINGS
});`)
        return lines.join('\n')
    }

    lines.push(`const trendHeight = input.int({
    title: "Trend Height",
    code: "TREND_HEIGHT",
    defval: 50,
    minval: 1,
    group: GRP_SETTINGS
});`)

    if (normalized === 'DOUBLE_TOP') {
        lines.push(`const secondTop = input.string({
    title: "Second Top",
    code: "SECOND_TOP",
    defval: "Both",
    options: ["Higher", "Lower", "Both"],
    group: GRP_SETTINGS
});`)
    }
    if (normalized === 'DOUBLE_BOTTOM') {
        lines.push(`const secondBottom = input.string({
    title: "Second Bottom",
    code: "SECOND_BOTTOM",
    defval: "Both",
    options: ["Higher", "Lower", "Both"],
    group: GRP_SETTINGS
});`)
    }
    if (normalized === 'BEARISH_PENNANT' || normalized === 'BULLISH_PENNANT') {
        lines.push(`const maxRollback = input.int({
    title: "Max Rollback",
    code: "MAX_ROLLBACK",
    defval: 50,
    minval: 1,
    group: GRP_SETTINGS
});`)
    }

    if (
        normalized === 'DOUBLE_TOP'
        || normalized === 'DOUBLE_BOTTOM'
        || normalized.endsWith('_WEDGE')
    ) {
        lines.push(`const permissibleDeviation = input.int({
    title: "Permissible Deviation",
    code: "PERMISSIBLE_DEVIATION",
    defval: 15,
    minval: 1,
    group: GRP_SETTINGS
});`)
    }

    lines.push(`const inProgress = input.bool({
    title: "In Progress",
    code: "IN_PROGRESS",
    defval: true,
    group: GRP_SETTINGS
});`)

    return lines.join('\n')
}
